// promotion-drawer.js
// The promotion run drawer: kicks off POST /api/promotion/apply, shows a live
// per-asset log fed by promotion_progress WS events, and the final run status
// from the apply response / promotion_done. Supports Retry failed (re-apply
// failed/blocked assets), Fix-manually (prefill composer for one row), Ask-agent
// (auto-send one asset to the agent), Fix-N-with-agent (auto-send all failed),
// Download-log (client-side JSON export), and Reverse-run.
import { api } from "./api.js";
import { esc, escAttr } from "./state.js";

let _host = null;        // element the drawer renders into
let _active = false;     // true while a run is in flight or its result is displayed
let _assets = [];        // accumulated per-asset rows {type,name,status,action,error?}
let _route = { source: "", target: "" };
let _confirm = "";       // typed confirm value retained for retry
let _lastRec = null;     // last completed run record (for retryFailed)
let _fixInFlight = false; // true while agent fix-all is in progress
let _fixDone = false;     // true after agent fix-all completed (drives emphasis + note)
let _reverseConfirmPending = false;  // true while waiting for user to confirm the reverse
let _reverseInFlight = false;        // true while reverse API call is in progress
let _reverseResult = null;           // last reverser result for display
// Per-item delete state: step entry_id -> "pending_confirm" | "in_flight" | "deleted" | "error:<msg>"
let _stepDeleteState = {};

// Status → icon mask name (all exist in icons.css).
// running uses ic-loader (the spinner) as the in-progress indicator;
// blocked/skipped use ic-circle (neutral hold); failed uses ic-x.
const _GLYPH = {
  applied:  "check",
  in_sync:  "check",
  failed:   "x",
  blocked:  "circle",
  skipped:  "circle",
  running:  "loader",
};

/** Mount the drawer into a host element. Call once from the owning panel. */
export function mountRunDrawer(hostEl) {
  _host = hostEl;
  // Reset fix state on each new mount so a fresh run starts clean.
  _fixInFlight = false;
  _fixDone = false;
  _reverseConfirmPending = false;
  _reverseInFlight = false;
  _reverseResult = null;
  // Register the completion listener once; idempotent via the named handler.
  window.removeEventListener("promotion-fix-complete", _onFixComplete);
  window.addEventListener("promotion-fix-complete", _onFixComplete);
}

function _onFixComplete() {
  _fixInFlight = false;
  _fixDone = true;
  _render({ phase: "done", rec: _lastRec });
}

/** True while a promotion is in flight or its drawer is displayed (for M1 guard). */
export function isRunActive() { return _active; }

/**
 * Kick off a promotion run.
 * @param {{source: string, target: string, confirm?: string, includeOptin?: string[]}} opts
 *   confirm — typed target tenant name (prod gate); empty/omitted for non-prod
 *   includeOptin — opt-in asset types to include (e.g. ["user_types"])
 */
export async function startPromotion({ source, target, confirm, includeOptin }) {
  _confirm = confirm || "";
  const opts = { confirm: _confirm };
  if (Array.isArray(includeOptin) && includeOptin.length) opts.include_optin = includeOptin;
  await _run(source, target, opts);
}

/**
 * Re-apply only the failed/blocked assets from the last completed run.
 * No-op if there is no previous run or no failed/blocked assets.
 */
export async function retryFailed() {
  if (!_lastRec || !_route.source) return;
  const names = (Array.isArray(_lastRec.results) ? _lastRec.results : [])
    .filter(r => r.status === "failed" || r.status === "blocked")
    .map(r => r.name);
  if (names.length === 0) return;
  await _run(_route.source, _route.target,
    { assets: names, release_id: _lastRec.release_id, confirm: _confirm });
}

async function _run(source, target, opts) {
  if (!_host) return;
  _active = true; _assets = []; _route = { source, target };
  _fixInFlight = false; _fixDone = false;
  _reverseConfirmPending = false; _reverseInFlight = false; _reverseResult = null;
  _stepDeleteState = {};
  _render({ phase: "running", rec: null });
  let rec;
  try {
    rec = await api.promotionApply(source, target, opts || {});
  } catch (e) {
    rec = { status: "error", error: String((e && e.message) || e) };
  }
  // promotion_done WS may also arrive concurrently; we render whichever
  // resolves last — the HTTP response is authoritative for the final state.
  _renderFinal(rec);
}

/**
 * WS handler for promotion_progress events.
 * asset shape: { type, name, status, action }
 */
export function onPromotionProgress(asset) {
  if (!_active || !asset) return;
  const i = _assets.findIndex(a => a.name === asset.name && a.type === asset.type);
  if (i >= 0) {
    _assets[i] = { ..._assets[i], ...asset };
  } else {
    _assets.push(asset);
  }
  _render({ phase: "running", rec: null });
}

/**
 * WS handler for promotion_done events.
 * run shape: the full run record (same as the apply response body).
 */
export function onPromotionDone(run) {
  if (!_active) return;
  _renderFinal(run || {});
}

// ---------------------------------------------------------------------------
// Internal render helpers
// ---------------------------------------------------------------------------

function _renderFinal(rec) {
  // If the response carries a results array, use it as the authoritative asset list.
  if (Array.isArray(rec && rec.results)) {
    _assets = rec.results;
    _lastRec = rec;
  }
  _active = false;
  _render({ phase: "done", rec });
}

function _statusPill(rec) {
  if (!rec) {
    return `<span class="prd-pill prd-pill-run">running</span>`;
  }
  if (rec.error || rec.gate) {
    const label = rec.gate ? "blocked" : "error";
    return `<span class="prd-pill prd-pill-err">${esc(label)}</span>`;
  }
  const s = String(rec.status || "running");
  const cls = s === "ok"      ? "ok"
            : s === "partial" ? "warn"
            : s === "empty"   ? "muted"
            : s === "error"   ? "err"
            : "run";
  return `<span class="prd-pill prd-pill-${cls}">${esc(s)}</span>`;
}

function _render({ phase, rec }) {
  if (!_host) return;

  const rows = _assets.map(a => {
    const st = String(a.status || "running");
    const g = _GLYPH[st] || "circle";
    const errLine = a.error
      ? `<div class="prd-err">${esc(a.error)}</div>`
      : "";
    // Per-failed/blocked row: two distinct actions:
    //   prd-fixmanual (ic-edit)     = prefill composer, no auto-send (fix yourself)
    //   prd-ask       (ic-sparkles) = auto-send THIS asset to agent
    const rowActions = (st === "failed" || st === "blocked")
      ? `<button class="prd-fixmanual" data-asset-name="${escAttr(a.name)}"
            title="Fix manually — load this failure into the composer to handle yourself"
          ><span class="ic ic-sm ic-edit" aria-hidden="true"></span></button>` +
        `<button class="prd-ask" data-asset-name="${escAttr(a.name)}"
            title="Ask the agent to fix this"
          ><span class="ic ic-sm ic-sparkles" aria-hidden="true"></span></button>`
      : "";
    return `<div class="prd-row prd-${esc(st)}">
      <span class="ic ic-sm ic-${g} prd-glyph" aria-hidden="true"></span>
      <span class="prd-name">${esc(a.name || "")}</span>
      <span class="prd-type">${esc(a.type || "")}</span>
      <span class="prd-status">${esc(st)}</span>
      ${rowActions}
      ${errLine}
    </div>`;
  }).join("");

  // Gate/error banner — uses a distinct class to avoid collision with .prd-blocked row.
  const gateBanner = (rec && (rec.error || rec.gate))
    ? `<div class="prd-gate-err">${esc(rec.error || ("Blocked by gate: " + rec.gate))}</div>`
    : "";

  const progressLine = phase === "running"
    ? `<span class="prd-prog">${_assets.length} asset${_assets.length === 1 ? "" : "s"}…</span>`
    : "";

  const emptyContent = rows
    ? rows
    : phase === "running"
      ? `<div class="prd-loading">Promoting…</div>`
      : `<div class="prd-empty">Nothing to promote.</div>`;

  const failedCount = phase === "done"
    ? (Array.isArray(_assets) ? _assets : [])
        .filter(a => a.status === "failed" || a.status === "blocked").length
    : 0;
  const retryFoot = (phase === "done" && failedCount > 0)
    ? `<div class="prd-foot">
        ${_fixDone ? `<span class="prd-fix-note">Agent finished — review its changes, then Retry</span>` : ""}
        <button class="prd-fix" ${_fixInFlight ? "disabled" : ""}>
          <span class="ic ic-sm ic-sparkles" aria-hidden="true"></span>
          Fix ${failedCount} with agent
        </button>
        <button class="prd-retry${_fixDone ? " prd-retry-emphasis" : ""}">Retry ${failedCount}</button>
      </div>`
    : "";

  // "Download log" footer — visible once a run is complete (phase=done, any result)
  const dlFoot = (phase === "done" && _lastRec)
    ? `<div class="prd-foot prd-dl-foot">
        <button class="prd-dl">
          <span class="ic ic-sm ic-download" aria-hidden="true"></span>
          Download log
        </button>
      </div>`
    : "";

  // "Reverse this run" footer — visible when run completed and has a run_id
  const canReverse = phase === "done" && _lastRec && _lastRec.run_id;
  let reverseFoot = "";
  if (canReverse) {
    if (_reverseResult) {
      const rev = _reverseResult;
      const revNote = rev.note
        ? `<span class="prd-rev-note">${esc(rev.note)}</span>`
        : `<span class="prd-rev-note">Reversed ${rev.reversed || 0} / Skipped ${rev.skipped || 0}</span>`;

      // Per-step delete controls for steps that are not "reversed"
      const stepRows = Array.isArray(rev.steps)
        ? rev.steps.filter(s => s.status !== "reversed" && s.target && Object.keys(s.target).length > 0)
        : [];
      const stepHtml = stepRows.map(s => {
        const eid = esc(String(s.entry_id || ""));
        const label = esc(s.name || s.family || s.tool || "");
        const ds = _stepDeleteState[s.entry_id];
        const idKey = Object.keys(s.target || {})[0];
        const idVal = idKey ? s.target[idKey] : null;
        if (!idKey || idVal == null) return "";
        if (ds === "deleted") {
          return `<div class="prd-delitem-row prd-delitem-done">
            <span class="ic ic-sm ic-check prd-delitem-glyph" aria-hidden="true"></span>
            <span class="prd-delitem-name">${label}</span>
            <span class="prd-delitem-badge">deleted</span>
          </div>`;
        }
        if (ds && ds.startsWith("error:")) {
          return `<div class="prd-delitem-row prd-delitem-err">
            <span class="ic ic-sm ic-x prd-delitem-glyph" aria-hidden="true"></span>
            <span class="prd-delitem-name">${label}</span>
            <span class="prd-delitem-errmsg">${esc(ds.slice(6))}</span>
          </div>`;
        }
        if (ds === "in_flight") {
          return `<div class="prd-delitem-row">
            <span class="ic ic-sm ic-loader prd-delitem-glyph" aria-hidden="true"></span>
            <span class="prd-delitem-name">${label}</span>
            <span class="prd-delitem-badge">deleting…</span>
          </div>`;
        }
        if (ds === "pending_confirm") {
          return `<div class="prd-delitem-row prd-delitem-confirming">
            <span class="prd-delitem-name">${label}</span>
            <button class="prd-delitem-ok" data-entry-id="${eid}"
              data-family="${escAttr(s.family || "")}" data-asset-id="${escAttr(String(idVal))}">
              <span class="ic ic-sm ic-trash" aria-hidden="true"></span> Delete
            </button>
            <button class="prd-delitem-cancel" data-entry-id="${eid}">Cancel</button>
          </div>`;
        }
        // default: show the Delete button
        const statusLabel = esc(s.status || "");
        return `<div class="prd-delitem-row">
          <span class="ic ic-sm ic-x prd-delitem-glyph" aria-hidden="true"></span>
          <span class="prd-delitem-name">${label}</span>
          <span class="prd-delitem-status">${statusLabel}</span>
          <button class="prd-delitem" data-entry-id="${eid}"
            title="Delete this asset from ${esc(_route.target)}">
            <span class="ic ic-sm ic-trash" aria-hidden="true"></span>
          </button>
        </div>`;
      }).join("");

      const stepsSection = stepHtml
        ? `<div class="prd-delitem-list">${stepHtml}</div>`
        : "";

      reverseFoot = `<div class="prd-foot prd-rev-foot">${revNote}${stepsSection}</div>`;
    } else if (_reverseConfirmPending) {
      reverseFoot = `<div class="prd-foot prd-rev-foot">
        <span class="prd-rev-label">Type <code>reverse</code> to confirm:</span>
        <input class="prd-rev-input" type="text" placeholder="reverse" autocomplete="off" />
        <button class="prd-rev-confirm" disabled>
          <span class="ic ic-sm ic-undo" aria-hidden="true"></span>
          Confirm
        </button>
        <button class="prd-rev-cancel">Cancel</button>
      </div>`;
    } else {
      reverseFoot = `<div class="prd-foot prd-rev-foot">
        <button class="prd-reverse" ${_reverseInFlight ? "disabled" : ""}>
          <span class="ic ic-sm ic-undo" aria-hidden="true"></span>
          Reverse this run
        </button>
      </div>`;
    }
  }

  _host.innerHTML = `
    <div class="prd-drawer">
      <div class="prd-head">
        ${_statusPill(phase === "running" ? null : rec)}
        <span class="prd-route">
          <span class="pl-mono">${esc(_route.source)}</span>
          <span class="prd-arrow"> → </span>
          <span class="pl-mono">${esc(_route.target)}</span>
        </span>
        ${progressLine}
      </div>
      ${gateBanner}
      <div class="prd-log">${emptyContent}</div>
      ${retryFoot}
      ${dlFoot}
      ${reverseFoot}
    </div>`;
}

/**
 * Find an asset by name in the current run's result list and dispatch a
 * 'promotion-ask-agent' window event so main.js can prefill the composer.
 * No-op if the asset is not found.
 * @param {string} name
 */
export function askAgentForAsset(name) {
  const a = (Array.isArray(_assets) ? _assets : []).find(x => x.name === name);
  if (!a) return;
  window.dispatchEvent(new CustomEvent("promotion-ask-agent", { detail: {
    type: a.type, name: a.name, error: a.error,
    source: _route.source, target: _route.target,
  } }));
}

/**
 * Auto-send a single failed/blocked asset to the agent (sparkle per-row button).
 * Finds the asset by name, dispatches 'promotion-fix-agent' with that one asset
 * so main.js → fixWithAgent() auto-sends a targeted fix prompt.
 * @param {string} name
 */
export function fixAssetWithAgent(name) {
  const a = (Array.isArray(_assets) ? _assets : []).find(x => x.name === name);
  if (!a) return;
  window.dispatchEvent(new CustomEvent("promotion-fix-agent", { detail: {
    assets: [{ type: a.type, name: a.name, error: a.error }],
    source: _route.source,
    target: _route.target,
  } }));
}

/**
 * Build a serializable log object from the current run state.
 * Pure function — no side effects. Used by downloadRunLog() and unit tests.
 * @returns {{ source: string, target: string, run_id: string, status: string, results: Array }}
 */
export function buildRunLog() {
  return {
    source:  _route.source || "",
    target:  _route.target || "",
    run_id:  (_lastRec && _lastRec.run_id)  || "",
    status:  (_lastRec && _lastRec.status)  || "",
    results: (Array.isArray(_assets) ? _assets : []).map(a => ({
      type:   a.type   || "",
      name:   a.name   || "",
      status: a.status || "",
      action: a.action || "",
      error:  a.error  || "",
    })),
  };
}

/**
 * Trigger a client-side download of the current run's log as a JSON file.
 * No backend call — pure browser download via Blob + Object URL.
 */
export function downloadRunLog() {
  const log  = buildRunLog();
  const json = JSON.stringify(log, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  const rid  = log.run_id || "log";
  a.href     = url;
  a.download = `promotion-${log.source}-to-${log.target}-${rid}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Dispatch a 'promotion-fix-agent' window event to main.js for ALL failed/blocked
 * assets, which auto-sends a consolidated fix prompt to the coding agent.
 * Sets _fixInFlight so the completion event can re-render with the Retry emphasis.
 */
export function fixFailuresWithAgent() {
  const rows = (Array.isArray(_assets) ? _assets : [])
    .filter(a => a.status === "failed" || a.status === "blocked")
    .map(a => ({ type: a.type, name: a.name, error: a.error }));
  if (rows.length === 0) return;
  _fixInFlight = true;
  _fixDone = false;
  window.dispatchEvent(new CustomEvent("promotion-fix-agent", { detail: {
    assets: rows,
    source: _route.source,
    target: _route.target,
  } }));
}

/** Begin the reverse confirm flow (show the confirm input). */
export function beginReverse() {
  _reverseConfirmPending = true;
  _reverseResult = null;
  _render({ phase: "done", rec: _lastRec });
}

/** Cancel the reverse confirm flow. */
export function cancelReverse() {
  _reverseConfirmPending = false;
  _render({ phase: "done", rec: _lastRec });
}

/** Execute the reverse after user has confirmed. */
export async function reverseRun() {
  if (!_lastRec || !_lastRec.run_id || !_route.target) return;
  _reverseConfirmPending = false;
  _reverseInFlight = true;
  _stepDeleteState = {};
  _render({ phase: "done", rec: _lastRec });
  let result;
  try {
    result = await api.promotionReverse(_route.target, _lastRec.run_id, { include_risky: false });
  } catch (e) {
    result = { reversed: 0, skipped: 0, note: String((e && e.message) || e) };
  }
  _reverseInFlight = false;
  _reverseResult = result;
  _render({ phase: "done", rec: _lastRec });
}

/**
 * Initiate per-item delete confirm for a reverse step.
 * Shows confirm button for the given step entry_id.
 * @param {string} entryId
 */
export function beginDeleteStep(entryId) {
  _stepDeleteState[entryId] = "pending_confirm";
  _render({ phase: "done", rec: _lastRec });
}

/**
 * Cancel per-item delete for a step.
 * @param {string} entryId
 */
export function cancelDeleteStep(entryId) {
  delete _stepDeleteState[entryId];
  _render({ phase: "done", rec: _lastRec });
}

/**
 * Execute per-item delete for a non-reversed reverse step.
 * @param {string} entryId  - the step's entry_id
 * @param {string} family   - asset family (e.g. "global_attribute")
 * @param {string|number} assetId - the id value from step.target
 */
export async function confirmDeleteStep(entryId, family, assetId) {
  _stepDeleteState[entryId] = "in_flight";
  _render({ phase: "done", rec: _lastRec });
  let ok = false, errMsg = "";
  try {
    const res = await api.promotionDeleteItem(_route.target, family, assetId, { confirm: "delete" });
    ok = !!(res && res.deleted);
    if (!ok) errMsg = (res && res.error) || "delete failed";
  } catch (e) {
    errMsg = String((e && e.message) || e);
  }
  _stepDeleteState[entryId] = ok ? "deleted" : `error:${errMsg}`;
  _render({ phase: "done", rec: _lastRec });
}
